import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { HeldCount, PagePending } from "@/client/components/page-pending";
import { dayOf } from "@/client/lib/dates";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData } from "@/client/lib/session";
import { peopleQuery } from "@/features/people/people-query";
import { NewProjectDialog } from "@/features/tasks/components/new-project-dialog";
import {
  ProjectsHeading,
  ProjectsPage,
  ProjectsTabs,
  ProjectsToolbar,
} from "@/features/tasks/components/projects-page";
import { ProjectsTableSkeleton } from "@/features/tasks/components/projects-table";
import { tasksCopy } from "@/features/tasks/copy";
import { clientsQuery, projectsQuery } from "@/features/tasks/projects-query";
import {
  PROJECTS_SEARCH_DEFAULTS,
  projectsSearch,
} from "@/features/tasks/schemas";

export const Route = createFileRoute("/_authenticated/$slug/projects/")({
  head: ({ match }) => ({
    meta: [
      { title: pageTitle(tasksCopy.projects.title, match.context.companyName) },
    ],
  }),
  validateSearch: projectsSearch,
  search: { middlewares: [stripSearchParams(PROJECTS_SEARCH_DEFAULTS)] },
  loader: ({ context }) =>
    Promise.all([
      ensureTenantData(context.queryClient, projectsQuery),
      ensureTenantData(context.queryClient, peopleQuery),
      ensureTenantData(context.queryClient, clientsQuery),
    ]),
  pendingMs: PENDING_MS,
  pendingComponent: ProjectsPending,
  component: Projects,
});

/** The tabs and New project for real; the counts wait on the projects unless they were read. */
function ProjectsPending() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const projects = useQueryClient().getQueryData(projectsQuery.queryKey);
  const count = (archived: boolean) =>
    projects ? (
      projects.filter((project) => Boolean(project.archivedAt) === archived)
        .length
    ) : (
      <HeldCount />
    );
  return (
    <PagePending
      shape="list"
      className="pb-12"
      header={
        <>
          <ProjectsHeading />
          <ProjectsToolbar
            tabs={
              <ProjectsTabs
                view={search.view}
                onSearch={(change) =>
                  navigate({ search: { ...search, ...change } })
                }
                active={count(false)}
                archived={count(true)}
              />
            }
            onNew={() => navigate({ search: { ...search, new: true } })}
          />
        </>
      }
    >
      <ProjectsTableSkeleton />
    </PagePending>
  );
}

function Projects() {
  const { slug } = Route.useParams();
  const { companyTimezone, email } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: projects } = useSuspenseQuery(projectsQuery);
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { data: clients } = useSuspenseQuery(clientsQuery);
  const peopleById = new Map(people.map((person) => [person.id, person]));
  const today = dayOf(new Date(), companyTimezone);

  return (
    <>
      <ProjectsPage
        slug={slug}
        projects={projects}
        people={peopleById}
        clients={new Map(clients.map((client) => [client.id, client.name]))}
        today={today}
        search={search}
        onSearch={(change) => navigate({ search: { ...search, ...change } })}
      />
      <NewProjectDialog
        open={search.new}
        people={people.filter(
          (person) => person.kind === "person" && person.state === "active",
        )}
        clients={clients}
        meId={people.find((person) => person.email === email)?.id ?? null}
        timeZone={companyTimezone}
        today={today}
        onClose={() => navigate({ search: { ...search, new: false } })}
        onCreated={(projectId) =>
          navigate({
            to: "/$slug/projects/$projectId",
            params: { slug, projectId },
          })
        }
      />
    </>
  );
}
